import styled, { css } from 'styled-components';
import {
  Media,
  TextStylesMobile,
  TextStylesDesktop,
  IconsSizes,
  Colors,
} from 'enums';
import {
  IconPositionInInput,
  BorderRadiusInput,
  IInputProps,
} from './Input.model';

type IInputWrapper = {
  error?: string;
  disabled?: boolean;
  border?: BorderRadiusInput;
};

export const InputWrapper = styled.div<IInputWrapper>`
  position: relative;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  row-gap: 4px;
  width: 100%;

  outline: none;
  border: none;

  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }

  ${({ border }) => {
    if (border === BorderRadiusInput.left) {
      return css`
        border-radius: 8px 0 0 8px;
        @media ${Media.notebook} {
          border-radius: 10px 0 0 10px;
        }
      `;
    }

    if (border === BorderRadiusInput.right) {
      return css`
        border-radius: 0 8px 8px 0;
        @media ${Media.notebook} {
          border-radius: 0 10px 10px 0;
        }
      `;
    }

    return css`
      border-radius: 8px;
      @media ${Media.notebook} {
        border-radius: 10px;
      }
    `;
  }};
`;

export const IconBox = styled.div<{ iconPosition: IconPositionInInput }>`
  position: absolute;
  pointer-events: none;

  top: 12px;
  ${({ iconPosition }) =>
    iconPosition === IconPositionInInput.left
      ? css`
          left: 16px;
        `
      : css`
          right: 16px;
        `}

  @media ${Media.notebook} {
    top: 12px;
    ${({ iconPosition }) =>
      iconPosition === IconPositionInInput.left
        ? css`
            left: 26px;
          `
        : css`
            right: 26px;
          `}
  }
`;

export const StyledInput = styled.input<IInputProps>`
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 48px;
  padding: ${({ icon, iconPosition }) => {
    if (icon && iconPosition === IconPositionInInput.left) {
      return `14px 16px 14px ${16 + Number(IconsSizes.M.slice(0, 2)) + 8}px`;
    }

    return `14px 16px`;
  }};

  ${TextStylesMobile.body}
  color: ${Colors.gray1};

  background-color: ${Colors.white};
  border: none;
  border-radius: inherit;

  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  /* Setting for input type number => Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  /* Setting for input type number => Firefox */
  -moz-appearance: textfield;

  user-select: none;
  -webkit-user-select: auto;
  -moz-user-select: none;

  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
  }

  &::-ms-reveal {
    display: none;
  }

  &:focus {
    outline: 1px solid ${Colors.yellow1};
  }

  &::placeholder {
    color: ${Colors.gray2};
  }

  @media ${Media.notebook} {
    height: 56px;
    padding: ${({ icon, iconPosition }) => {
      if (icon && iconPosition === IconPositionInInput.left) {
        return `16px 24px 16px ${24 + Number(IconsSizes.L.slice(0, 2)) + 16}px`;
      }

      return `16px 24px`;
    }};

    ${TextStylesDesktop.body}

    background-color: ${Colors.background};

    &:focus {
      background-color: ${Colors.white};
    }

    ${({ customStyles }) => customStyles};
  }

  ${({ error }) =>
    error &&
    css`
      outline: 1px solid ${Colors.red1} !important;
    `}

  ${({ customStyles }) => customStyles};
`;

export const SubLabelContainer = styled.div`
  ${TextStylesMobile.small1}
  color: ${({ theme }) => theme.colors.gray3};
  cursor: text;

  @media ${Media.notebook} {
    ${TextStylesDesktop.small1};
  }
`;
